/**
 * 웹훅 서명 키 [값 입력]·[새로 생성] 창 — D-9·D-10·D-11.
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import { REL_NOTICE, SECRET_MAX, SECRET_MIN, validateWebhookSecret } from "utils/partnerAdmin";
import {
  CheckLabel,
  ErrorMsg,
  Field,
  FieldLabel,
  Hint,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Notice,
  TextInput,
} from "./style";

// ---------------------------------------------------------------------------
// [값 입력] — 상대가 준 값을 넣는다
// ---------------------------------------------------------------------------

interface WebhookSecretInputModalProps {
  isRel: boolean;
  onSubmit: (secret: string) => Promise<void>;
  onCancel: () => void;
}

export const WebhookSecretInputModal: React.FC<WebhookSecretInputModalProps> = ({ isRel, onSubmit, onCancel }) => {
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const check = validateWebhookSecret(secret);
    setError(check.ok ? undefined : check.message);
    if (!check.ok) return;
    setSubmitting(true);
    try {
      await onSubmit(secret);
    } catch {
      // 실패 사유는 훅이 토스트로 보여준다.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody data-testid="webhook-secret-input">
      <ModalTitle>웹훅 서명 키 값 입력</ModalTitle>
      {isRel && <Notice $tone="warn">{REL_NOTICE}</Notice>}
      <Field>
        <FieldLabel>서명 키</FieldLabel>
        <TextInput
          type="password"
          autoComplete="off"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          data-testid="webhook-secret-input-value"
        />
        <Hint>
          상대가 준 값을 그대로 붙여 넣습니다. {SECRET_MIN}~{SECRET_MAX}자. 저장하면 다음 결과 통지부터 이 값으로
          서명합니다.
        </Hint>
        {error && <ErrorMsg data-testid="webhook-secret-input-error">{error}</ErrorMsg>}
      </Field>
      <ModalFooter>
        <Button type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="webhook-secret-input-submit"
        >
          저장
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};

// ---------------------------------------------------------------------------
// [새로 생성] — 서버가 만든다. 자동 전달 주소가 있으면 상대 서버에 먼저 보낸다(D-11).
// ---------------------------------------------------------------------------

export const GENERATE_WITH_PROVISION_TEXT = "상대 서버에 먼저 보내고, 성공하면 PhAI도 새 값으로 바꿉니다";
export const GENERATE_WITHOUT_PROVISION_TEXT = "상대가 새 값으로 바꿀 때까지 결과 통지가 거절됩니다";

interface GenerateSecretModalProps {
  canProvision: boolean;
  isRel: boolean;
  /** provision — 자동 전달 주소가 없으면 undefined */
  onSubmit: (provision: boolean | undefined) => Promise<void>;
  onCancel: () => void;
}

export const GenerateSecretModal: React.FC<GenerateSecretModalProps> = ({
  canProvision,
  isRel,
  onSubmit,
  onCancel,
}) => {
  const [provision, setProvision] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const willProvision = canProvision && provision;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(canProvision ? provision : undefined);
    } catch {
      // 실패 사유는 훅이 토스트로 보여준다(전달 실패면 아무것도 바뀌지 않았다).
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody data-testid="webhook-secret-generate">
      <ModalTitle>웹훅 서명 키 새로 생성</ModalTitle>
      {isRel && <Notice $tone="warn">{REL_NOTICE}</Notice>}
      {canProvision && (
        <CheckLabel>
          <input
            type="checkbox"
            checked={provision}
            onChange={(e) => setProvision(e.target.checked)}
            data-testid="webhook-secret-generate-provision"
          />
          상대 서버에 바로 적용
        </CheckLabel>
      )}
      <Notice $tone={willProvision ? "info" : "warn"} data-testid="webhook-secret-generate-text">
        {willProvision ? GENERATE_WITH_PROVISION_TEXT : GENERATE_WITHOUT_PROVISION_TEXT}
      </Notice>
      <ModalFooter>
        <Button type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="webhook-secret-generate-submit"
        >
          새로 생성
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};
